import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useUpdateUser } from '../../../hooks/useUsers';
import { useQueryClient } from '@tanstack/react-query';
import { useQuestionsContext } from '../../../context/QuestionsContext';

export function useQuizSession(user, onUpdateUser, quizSize = 10, selectedDifficulties = [2, 3, 4]) {
  const { activeQuestions: questionsData, isLoading, error } = useQuestionsContext();
  const updateUserMutation = useUpdateUser();
  const updateUserMutationRef = useRef(updateUserMutation);
  updateUserMutationRef.current = updateUserMutation;
  const queryClient = useQueryClient();

  // --- Core quiz state ---
  const [questions, setQuestions] = useState([]);
  const [remainingQuestions, setRemainingQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isQuizComplete, setIsQuizComplete] = useState(false);
  const [answers, setAnswers] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [quizCreated, setQuizCreated] = useState(false);

  // Track if we've already saved to prevent duplicates
  const hasSavedRef = useRef(false);
  
  // Store refs for cleanup to access latest values (IMMEDIATE sync)
  const answersRef = useRef(answers);
  const userRef = useRef(user);
  const onUpdateUserRef = useRef(onUpdateUser);
  const previousUserIdRef = useRef(user?.userId);
  const previousDifficultiesRef = useRef(selectedDifficulties);

  // Sync refs immediately
  answersRef.current = answers;
  userRef.current = user;
  onUpdateUserRef.current = onUpdateUser;

  // --- Map question difficulty labels to numbers ---
  const DIFFICULTY_MAP = {
    'Easy': 1,
    'Medium': 2,
    'Hard': 3,
    'Very Hard': 4,
    'Impossible': 5
  };

  // --- Filter questions by difficulty ---
  const filteredQuestions = useMemo(() => {
    if (!questionsData || questionsData.length === 0) return [];
    return questionsData.filter(q => {
      
      // Handle both string labels and numeric values
      const difficultyValue = typeof q.difficulty === 'string' 
        ? DIFFICULTY_MAP[q.difficulty] 
        : q.difficulty;
      return selectedDifficulties.includes(difficultyValue);
    });
  }, [questionsData, selectedDifficulties]);

  // --- Shuffle helper ---
  const shuffleArray = useCallback((array) => {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }, []);

  // --- Initialize quiz batch ---
  const initializeQuiz = useCallback(() => {
    if (!filteredQuestions || filteredQuestions.length === 0) {
      setInitialized(false);
      return;
    }

    if (quizCreated) {
      console.log('🎯 Quiz already created, skipping re-initialization');
      return;
    }

    console.log('🆕 Creating new quiz...');
    const shuffled = shuffleArray(filteredQuestions);
    const initialBatch = shuffled.slice(0, quizSize);
    const rest = shuffled.slice(quizSize);

    setQuestions(initialBatch);
    setRemainingQuestions(rest);
    setCurrentIndex(0);
    setAnswers({});
    setIsQuizComplete(false);
    setInitialized(true);
    setQuizCreated(true);
    hasSavedRef.current = false;
  }, [filteredQuestions, quizSize, shuffleArray, quizCreated]);

  useEffect(() => {
    initializeQuiz();
  }, [initializeQuiz]);

  // CRITICAL: Reset quiz when user changes (login/logout/switch)
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    const currentUserId = user?.userId;
    
    console.log('🔍 User change check:', {
      previousUserId: previousUserId || 'none',
      currentUserId: currentUserId || 'none',
      quizCreated,
      changed: previousUserId !== currentUserId
    });
    
    // User changed - reset the quiz
    if (previousUserId !== currentUserId && quizCreated) {
      console.log('👤 User changed - resetting quiz:', {
        from: previousUserId || 'no user',
        to: currentUserId || 'no user',
        username: user?.username || 'none'
      });
      
      // Force quiz reset
      setQuizCreated(false);
      hasSavedRef.current = false;
    }
    
    // Update previous user ID
    previousUserIdRef.current = currentUserId;
  }, [user?.userId, quizCreated]);

  // CRITICAL: Reset quiz when difficulty selection changes
  useEffect(() => {
    const previousDifficulties = previousDifficultiesRef.current;
    const difficultiesChanged = 
      previousDifficulties.length !== selectedDifficulties.length ||
      !previousDifficulties.every(d => selectedDifficulties.includes(d));

    if (difficultiesChanged && quizCreated) {
      console.log('🎚️ Difficulty changed - resetting quiz:', {
        from: previousDifficulties,
        to: selectedDifficulties
      });

      saveTotals();
      
      // Force quiz reset
      setQuizCreated(false);
      hasSavedRef.current = false;
    }

    // Update previous difficulties
    previousDifficultiesRef.current = selectedDifficulties;
  }, [selectedDifficulties, quizCreated]);

  // --- Derived values ---
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex] || null;
  const hasAnswered = !!currentAnswer;
  const score = useMemo(
    () => Object.values(answers).filter((a) => a.isCorrect).length,
    [answers]
  );

  // Get number of answered questions
  const answeredCount = Object.keys(answers).length;

  // --- Submit answer ---
  const submitAnswer = useCallback(
    (rawUserInput, validateFn) => {
      try {
        if (!initialized || isQuizComplete || !currentQuestion) return { success: false };

        const trimmed = String(rawUserInput || '').trim();
        if (!trimmed) return { success: false };

        const isMC = currentQuestion.questionType === 'MultipleChoice';
        const answersList = currentQuestion.correctAnswers;

        let isCorrect = false;
        let correctAnswerDisplay = '';

        if (isMC) {
          // trimmed is the selected option id e.g. 'a'
          isCorrect = Array.isArray(answersList) && answersList.includes(trimmed);
          // Resolve the correct option id back to display text for AnswerCard
          const correctOption = currentQuestion.mcOptions?.find(
            (opt) => opt.id === answersList[0]
          );
          correctAnswerDisplay = correctOption?.text ?? answersList[0];
        } else {
          isCorrect = validateFn(trimmed, answersList);
          correctAnswerDisplay = Array.isArray(answersList)
            ? answersList[0] ?? ''
            : answersList;
        }

        // For MC, show the selected option's display text rather than the raw id
        const userAnswerDisplay = isMC
          ? currentQuestion.mcOptions?.find((opt) => opt.id === trimmed)?.text ?? trimmed
          : trimmed;

        const answerData = {
          questionId: currentQuestion.questionId ?? null,
          questionText: currentQuestion.text ?? '',
          userAnswer: userAnswerDisplay,
          isCorrect,
          correctAnswer: correctAnswerDisplay,
          submittedAt: new Date().toISOString(),
        };

        setAnswers((prev) => ({ ...prev, [currentIndex]: answerData }));

        return { success: true, answerData };
      } catch (e) {
        console.error('submitAnswer error:', e);
        return { success: false };
      }
    },
    [initialized, isQuizComplete, currentQuestion, currentIndex]
  );

  // --- Navigation ---
  const goToNextQuestion = useCallback(() => {
    if (!initialized || totalQuestions === 0) return;
    if (currentIndex + 1 < totalQuestions) setCurrentIndex((prev) => prev + 1);
    else setIsQuizComplete(true);
  }, [initialized, totalQuestions, currentIndex]);

  const goToPrevQuestion = useCallback(() => {
    if (!initialized || totalQuestions === 0) return;
    if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
  }, [initialized, totalQuestions, currentIndex]);

  const resetQuiz = useCallback(() => {
    if (!filteredQuestions || filteredQuestions.length === 0) return;
    
    console.log('🔄 Resetting quiz...');
    const reshuffled = shuffleArray(filteredQuestions);
    setQuestions(reshuffled.slice(0, quizSize));
    setRemainingQuestions(reshuffled.slice(quizSize));
    setCurrentIndex(0);
    setAnswers({});
    setIsQuizComplete(false);
    setInitialized(true);
    setQuizCreated(true);
    hasSavedRef.current = false;
  }, [filteredQuestions, quizSize, shuffleArray]);

  // --- Save totals ---
  const saveTotals = useCallback(() => {
      if (hasSavedRef.current) {
        console.log('⏭️ Skipping save - already saved');
        return;
      }

      // Read from refs to avoid stale closure issues
      const currentUser = userRef.current;
      const currentAnswers = answersRef.current;
      const currentAnsweredCount = Object.keys(currentAnswers).length;
      const currentScore = Object.values(currentAnswers).filter((a) => a.isCorrect).length;

      if (!currentUser?.userId || currentAnsweredCount === 0) {
        console.log('⏭️ Skipping save - no user or no answers');
        return;
      }

      console.log('💾 Saving totals:', { currentAnsweredCount, currentScore });
      hasSavedRef.current = true;

      const statsUpdate = {
        userId: currentUser.userId,
        totalQuestionsAnswered: currentAnsweredCount,  // ← delta only
        totalCorrectAnswers: currentScore,             // ← delta only
      };

      updateUserMutationRef.current.mutate(statsUpdate, {  // ← fixed
        onSuccess: (backendUser) => {
          console.log('✅ User totals updated on backend', backendUser);
          localStorage.setItem('triviaUser', JSON.stringify(backendUser));
          onUpdateUserRef.current?.(backendUser); // We keep messing around with this - consider other approach if tempted to change again
        },
        onError: (err) => {
          console.error('❌ Failed to update user totals', err);
          hasSavedRef.current = false;
        },
      });
    }, []); // ← empty deps, reads from refs instead

  // --- Auto-save on quiz completion ---
  useEffect(() => {
    if (isQuizComplete && !hasSavedRef.current) {
      saveTotals();
    }
  }, [isQuizComplete, saveTotals]);

  // --- Save on unmount (when user navigates away) ---
  useEffect(() => {
    console.log('🎯 Quiz session mounted');
    // hasSavedRef.current = false;  // ← reset on every mount
    
    return () => {
      console.log('🚪 Quiz session unmounting...');
      
      const currentAnswers = answersRef.current;
      const currentUser = userRef.current;
      const currentOnUpdateUser = onUpdateUserRef.current;
      
      const currentAnsweredCount = Object.keys(currentAnswers).length;
      const currentScore = Object.values(currentAnswers).filter((a) => a.isCorrect).length;
      
      console.log('📊 Unmount state:', { 
        answeredCount: currentAnsweredCount, 
        score: currentScore, 
        hasSaved: hasSavedRef.current,
        hasUser: !!currentUser?.userId 
      });
      
      if (currentAnsweredCount > 0 && !hasSavedRef.current) {
        if (currentUser?.userId) {
          // User exists - save to their account
          console.log('💾 Saving progress on unmount for user:', currentUser.username);
          hasSavedRef.current = true;
          
          const statsUpdate = {
            userId: currentUser.userId,
            totalQuestionsAnswered: currentAnsweredCount,  // ← just the session count
            totalCorrectAnswers: currentScore,              // ← just the session score
          };

          updateUserMutationRef.current.mutate(statsUpdate, {
            onSuccess: (backendUser) => {
              console.log('✅ Unmount save successful');
              localStorage.setItem('triviaUser', JSON.stringify(backendUser));
              onUpdateUserRef.current?.(backendUser); // We keep messing around with this - consider other approach if tempted to change again
            },
            onError: (err) => {
              console.error('❌ Unmount save FAILED:', err?.response?.status, err?.response?.data);
              console.error('❌ Status:', err?.response?.status);
              console.error('❌ Data:', err?.response?.data);
            }
          });
        } else {
          // No user in ref - check if user was just registered in localStorage
          const justRegisteredUser = localStorage.getItem('triviaUser');
          
          if (justRegisteredUser) {
            try {
              const parsedUser = JSON.parse(justRegisteredUser);
              if (parsedUser.userId) {
                console.log('💾 User just registered - saving progress to:', parsedUser.username);
                hasSavedRef.current = true;
                
                const statsUpdate = {
                  userId: currentUser.userId,
                  totalQuestionsAnswered: currentAnsweredCount,  // ← just the session count
                  totalCorrectAnswers: currentScore,              // ← just the session score
                };
                
                updateUserMutationRef.current.mutate(statsUpdate, {  // ← fixed
                  onSuccess: (backendUser) => {
                    console.log('✅ New user save successful');
                    localStorage.setItem('triviaUser', JSON.stringify(backendUser));
                  },
                  onError: (err) => console.error('❌ New user save failed:', err)
                });
              }
            } catch (e) {
              console.error('Failed to parse just-registered user:', e);
            }
          }
        }
      } else {
        console.log('⏭️ No save needed on unmount');
      }
    };
  }, []);

  return {
    questions,
    currentQuestion,
    currentIndex,
    totalQuestions,
    isQuizComplete,
    remainingQuestionsCount: remainingQuestions.length,
    initialized,
    answers,
    currentAnswer,
    hasAnswered,
    score,
    answeredCount,
    submitAnswer,
    goToNextQuestion,
    goToPrevQuestion,
    resetQuiz,
    initializeQuiz,
    isLoading,
    error,
  };
}

export default useQuizSession;